import React, { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../../lib/supabase'
import authAPI from '../../lib/authAPI'
import { useAuth } from '../../contexts/AuthContext'

const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)
const isNonEmptyString = value => typeof value === 'string' && value.length > 0
const isStrictSuccess = response => isObject(response) && response.error === null && isObject(response.data)
const FACTOR_CAP_ERROR_CODE = 'too_many_enrolled_mfa_factors'
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const isValidFactor = factor => (
  isObject(factor) &&
  isNonEmptyString(factor.id) &&
  ['totp', 'phone'].includes(factor.factor_type) &&
  ['verified', 'unverified'].includes(factor.status)
)

const getStrictFactorList = response => {
  if (
    !isStrictSuccess(response) ||
    !Array.isArray(response.data.all) ||
    !Array.isArray(response.data.totp) ||
    !Array.isArray(response.data.phone) ||
    !response.data.all.every(isValidFactor) ||
    !response.data.totp.every(isValidFactor) ||
    !response.data.phone.every(isValidFactor)
  ) {
    throw new Error('Malformed MFA factor list')
  }

  const allIds = response.data.all.map(factor => factor.id)
  if (new Set(allIds).size !== allIds.length) {
    throw new Error('Duplicate MFA factors')
  }

  const expectedTotpIds = response.data.all
    .filter(factor => factor.factor_type === 'totp' && factor.status === 'verified')
    .map(factor => factor.id)
    .sort()
  const actualTotpIds = response.data.totp
    .filter(factor => factor.factor_type === 'totp' && factor.status === 'verified')
    .map(factor => factor.id)
    .sort()
  const expectedPhoneIds = response.data.all
    .filter(factor => factor.factor_type === 'phone' && factor.status === 'verified')
    .map(factor => factor.id)
    .sort()
  const actualPhoneIds = response.data.phone
    .filter(factor => factor.factor_type === 'phone' && factor.status === 'verified')
    .map(factor => factor.id)
    .sort()

  if (
    actualTotpIds.length !== response.data.totp.length ||
    actualPhoneIds.length !== response.data.phone.length ||
    JSON.stringify(actualTotpIds) !== JSON.stringify(expectedTotpIds) ||
    JSON.stringify(actualPhoneIds) !== JSON.stringify(expectedPhoneIds)
  ) {
    throw new Error('Malformed verified MFA factor projections')
  }

  return response.data.all
}

const getStrictEnrollment = response => {
  const data = response?.data
  if (
    !isStrictSuccess(response) ||
    !isNonEmptyString(data.id) ||
    data.type !== 'totp' ||
    !isObject(data.totp) ||
    !isNonEmptyString(data.totp.qr_code) ||
    !data.totp.qr_code.startsWith('data:image/svg+xml') ||
    !isNonEmptyString(data.totp.secret) ||
    !isNonEmptyString(data.totp.uri) ||
    !data.totp.uri.startsWith('otpauth://')
  ) {
    throw new Error('Malformed MFA enrollment result')
  }

  return data
}

const getSecureFriendlyName = usedNames => {
  const secureCrypto = window.crypto
  if (typeof secureCrypto?.randomUUID !== 'function') {
    throw new Error('Secure MFA enrollment naming is unavailable')
  }

  const uniqueId = secureCrypto.randomUUID()
  if (!UUID_V4_PATTERN.test(uniqueId)) {
    throw new Error('Secure MFA enrollment naming failed')
  }

  const friendlyName = `E8 Tracker MFA ${uniqueId}`
  if (usedNames.has(friendlyName)) {
    throw new Error('Secure MFA enrollment name was not unique')
  }
  usedNames.add(friendlyName)
  return friendlyName
}

const isFactorCapError = error => (
  isObject(error) && error.code === FACTOR_CAP_ERROR_CODE
)

const decodeJwtPayload = token => {
  const parts = token.split('.')
  if (parts.length !== 3 || !parts.every(isNonEmptyString)) {
    throw new Error('Malformed access token')
  }

  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
  return JSON.parse(window.atob(padded))
}

const getStrictUpgradedSession = response => {
  const data = response?.data
  if (
    !isStrictSuccess(response) ||
    !isNonEmptyString(data.access_token) ||
    !isNonEmptyString(data.refresh_token) ||
    typeof data.token_type !== 'string' ||
    data.token_type.toLowerCase() !== 'bearer' ||
    !Number.isFinite(data.expires_in) ||
    data.expires_in <= 0 ||
    !isObject(data.user) ||
    !isNonEmptyString(data.user.id)
  ) {
    throw new Error('Malformed upgraded MFA session')
  }

  const claims = decodeJwtPayload(data.access_token)
  if (!isObject(claims) || claims.aal !== 'aal2') {
    throw new Error('Session was not upgraded to AAL2')
  }

  return data
}

const MfaEnrollment = ({ onSuccess }) => {
  const { signOut } = useAuth()
  const [phase, setPhase] = useState('starting')
  const [enrollment, setEnrollment] = useState(null)
  const [verificationCode, setVerificationCode] = useState('')
  const [error, setError] = useState('')
  const mountedRef = useRef(false)
  const startedRef = useRef(false)
  const startingRef = useRef(false)
  const submittingRef = useRef(false)
  const actionRef = useRef(false)
  const abandonedRef = useRef(false)
  const friendlyNamesRef = useRef(new Set())

  const startEnrollment = useCallback(async () => {
    if (startingRef.current) return
    startingRef.current = true
    abandonedRef.current = false
    setPhase('starting')
    setEnrollment(null)
    setVerificationCode('')
    setError('')

    try {
      const mfa = supabase?.auth?.mfa
      if (
        !mfa?.listFactors ||
        !mfa?.enroll ||
        !mfa?.challengeAndVerify
      ) {
        throw new Error('MFA enrollment is unavailable')
      }

      const factors = getStrictFactorList(await mfa.listFactors())
      if (abandonedRef.current) return
      if (factors.some(item => item.status === 'verified')) {
        throw new Error('Verified factor contradicts MFA enrollment state')
      }

      const enrollResponse = await mfa.enroll({
        factorType: 'totp',
        friendlyName: getSecureFriendlyName(friendlyNamesRef.current),
      })

      if (abandonedRef.current) return
      if (isFactorCapError(enrollResponse?.error)) throw enrollResponse.error

      const enrollData = getStrictEnrollment(enrollResponse)

      if (mountedRef.current && !abandonedRef.current) {
        setEnrollment(enrollData)
        setPhase('ready')
      }
    } catch (startError) {
      console.error('MFA enrollment could not start:', startError)
      if (mountedRef.current && !abandonedRef.current) {
        setError(isFactorCapError(startError)
          ? 'Too many two-factor setup attempts are stored. Contact an administrator or support.'
          : 'Unable to start two-factor setup. Retry or sign out.')
        setPhase('error')
      }
    } finally {
      startingRef.current = false
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    if (!startedRef.current) {
      startedRef.current = true
      void startEnrollment()
    }

    return () => {
      mountedRef.current = false
    }
  }, [startEnrollment])

  const handleVerify = async () => {
    if (submittingRef.current || !/^\d{6}$/.test(verificationCode) || !enrollment) return
    submittingRef.current = true
    setError('')
    setPhase('verifying')

    try {
      const verifyResponse = await supabase.auth.mfa.challengeAndVerify({
        factorId: enrollment.id,
        code: verificationCode,
      })
      const upgradedSession = getStrictUpgradedSession(verifyResponse)

      authAPI.saveTokensToStorage(
        upgradedSession.access_token,
        upgradedSession.refresh_token
      )
      if (
        authAPI.token !== upgradedSession.access_token ||
        authAPI.refreshToken !== upgradedSession.refresh_token
      ) {
        throw new Error('Application token synchronization failed')
      }
      if (mountedRef.current && typeof onSuccess === 'function') {
        await onSuccess()
      }
    } catch (verifyError) {
      console.error('MFA enrollment verification failed:', verifyError)
      if (mountedRef.current) {
        setError('Verification failed. Start over or sign out.')
        setPhase('verification-error')
      }
    } finally {
      submittingRef.current = false
    }
  }

  const handleStartOver = async () => {
    if (actionRef.current || startingRef.current || submittingRef.current) return
    actionRef.current = true
    try {
      await startEnrollment()
    } finally {
      actionRef.current = false
    }
  }

  const handleSignOut = async () => {
    if (actionRef.current || submittingRef.current) return
    actionRef.current = true
    abandonedRef.current = true
    setError('')

    try {
      if (typeof signOut !== 'function') {
        throw new Error('Application sign out is unavailable')
      }
      const result = await signOut()
      if (result?.error) throw result.error
    } catch (signOutError) {
      console.error('MFA enrollment sign out failed:', signOutError)
      if (mountedRef.current) {
        setError('Sign out failed. Please try again.')
        setPhase('error')
      }
    } finally {
      actionRef.current = false
    }
  }

  const isReady = ['ready', 'verifying', 'verification-error'].includes(phase)

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow">
        <h1 className="text-xl font-semibold text-gray-900">Set up two-factor authentication</h1>
        <p className="mt-2 text-sm text-gray-600">
          Add E8 Tracker to your authenticator app before entering the application.
        </p>

        {phase === 'starting' && (
          <p className="mt-6 text-sm text-gray-600">Preparing secure setup...</p>
        )}

        {isReady && enrollment && (
          <div className="mt-6 space-y-5">
            <div className="flex justify-center">
              <img
                src={enrollment.totp.qr_code}
                alt="Authenticator QR code"
                width="200"
                height="200"
                className="rounded border bg-white p-2"
              />
            </div>
            <div>
              <p className="text-sm text-gray-600">Manual secret</p>
              <code className="mt-1 block break-all rounded bg-gray-100 p-3 text-sm text-gray-900">
                {enrollment.totp.secret}
              </code>
            </div>
            <div>
              <label htmlFor="enrollment-code" className="block text-sm font-medium text-gray-700">
                Six-digit verification code
              </label>
              <input
                id="enrollment-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={verificationCode}
                onChange={event => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={phase === 'verifying'}
                maxLength={6}
                className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-center font-mono text-lg"
              />
            </div>
            <button
              type="button"
              onClick={handleVerify}
              disabled={phase === 'verifying' || !/^\d{6}$/.test(verificationCode)}
              className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {phase === 'verifying' ? 'Verifying...' : 'Verify and continue'}
            </button>
          </div>
        )}

        {error && <p className="mt-5 text-sm text-red-700" role="alert">{error}</p>}

        <div className="mt-6 flex justify-center gap-3">
          {['error', 'verification-error'].includes(phase) && (
            <button
              type="button"
              onClick={handleStartOver}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white"
            >
              Start over
            </button>
          )}
          <button
            type="button"
            onClick={handleSignOut}
            disabled={phase === 'verifying'}
            className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}

export default MfaEnrollment
